/** Pieces every Templates surface shows the same way: the plain name and icon
 *  of an answer type, numbers and money in the app's locale, and a refusal
 *  from the server in the person's own language. */
import type { ReactNode } from 'react';
import {
  AlignLeftOutlined,
  CheckCircleOutlined,
  NumberOutlined,
  StarOutlined,
  TagOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { activeLocale } from '@/utils/locale';
import { blobErrorBody, triggerBlobDownload } from '@/utils/download';
import insightsService from '../../services/insightsService';
import type { ScopeParams } from '../../services/insightsService';
import type { InsightFilters, InsightTemplate, ResponseType, TenantNode } from '../../types/insights';
import { formatCount as formatCountIn, type ScaleRange } from '../../utils/chartModel';

type T = (key: string, options?: Record<string, unknown>) => string;

export const TYPE_ORDER: ResponseType[] = ['boolean', 'label', 'multi_label', 'scale', 'numeric', 'free_text'];

export const TYPE_ICON: Record<ResponseType, ReactNode> = {
  boolean: <CheckCircleOutlined />,
  label: <TagOutlined />,
  multi_label: <TagsOutlined />,
  scale: <StarOutlined />,
  numeric: <NumberOutlined />,
  free_text: <AlignLeftOutlined />,
};

/** "Yes / No", "Several categories"... - never the stored enum name. */
export function typeName(t: T, type: ResponseType): string {
  return t(`insights.tpl.type.${type}`);
}

export function formatCount(n: number): string {
  return formatCountIn(n, activeLocale());
}

/** A score's scale (its chart shows the whole range); none for other types. */
export function scaleRange(template: InsightTemplate): ScaleRange | undefined {
  if (template.response_type !== 'scale') return undefined;
  const options = (template.response_options || {}) as { min?: unknown; max?: unknown };
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  return { min: num(options.min), max: num(options.max) };
}

function usd(value: number, digits: number): string {
  return new Intl.NumberFormat(activeLocale(), {
    style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(value);
}

/** What a template or a report template covers, in a few words: "Team Alpha, Sales", "3 agents",
 *  "Engineering and its sub-organizations" (its owner's organization when it covers everything). */
export function scopeSummary(t: T, scope: { tenant_ids?: string[]; chatbot_ids?: string[] } | undefined,
                             tenantTree: TenantNode[], ownerName?: string): string {
  const agents = scope?.chatbot_ids?.length || 0;
  const orgs = scope?.tenant_ids || [];
  if (agents) return t('insights.tpl.results.scopeAgents', { count: agents, n: formatCount(agents) });
  if (orgs.length) {
    const names = orgs.map((id) => tenantTree.find((n) => n.tenant_id === id)?.tenant_name).filter(Boolean) as string[];
    if (names.length === orgs.length && names.length <= 2) return names.join(', ');
    return t('insights.tpl.results.scopeOrgs', { count: orgs.length, n: formatCount(orgs.length) });
  }
  return ownerName ? t('insights.tpl.results.scopeAll', { org: ownerName }) : t('insights.tpl.results.scopeAllPlain');
}

/** An amount that is exact (a budget): dollars and cents. */
export function formatUsd(value: number): string {
  return usd(value, 2);
}

/** An ESTIMATED cost, at the precision it has. Measured against the ledger
 *  (item 62: four real runs of 300 conversations), an estimate lands within
 *  about a quarter of the real cost - so tenths of a cent would be noise:
 *  "about $0.03" to the cent, whole dollars from $10, "under $0.01" below a
 *  cent (never "$0.00" for something that does cost). */
export function formatEstimate(t: T, value: number): string {
  if (value <= 0) return usd(0, 2);   // nothing to ask costs nothing
  if (value < 0.01) return t('insights.tpl.cost.under', { cost: usd(0.01, 2) });
  return t('insights.tpl.cost.about', { cost: usd(value, value >= 10 ? 0 : 2) });
}

/** A chart value as people read it: Yes / No for a boolean bar. */
export function valueLabel(t: T, type: ResponseType, value: string): string {
  if (type === 'boolean') return t(value === 'true' ? 'insights.tpl.chart.yes' : 'insights.tpl.chart.no');
  return value;
}

/** The server's coded refusal, if it gave one. */
export function errorCode(err: unknown): string | null {
  const code = (err as { response?: { data?: { error_code?: unknown } } })?.response?.data?.error_code;
  return typeof code === 'string' ? code : null;
}

/** A failed request in the person's language: the refusal's own wording
 *  (insights.tpl.errors.<CODE>) with its numbers, else by HTTP status. The
 *  server's English text is never shown. */
export function errorMessage(t: T, err: unknown, body?: Record<string, any>): string {
  const response = (err as { response?: { status?: number; data?: Record<string, any> } })?.response;
  const data = body || response?.data || {};
  const code = typeof data.error_code === 'string' ? data.error_code : null;
  // A code with a wording in the catalogue is worded; any other falls to its status.
  // A schedule's refusal is the reports' words (one schedule rule, one set of words).
  const key = code && code.startsWith('SCHEDULE_') && code !== 'SCHEDULE_PAUSED'
    ? `insights.reports.errors.${code}` : `insights.tpl.errors.${code}`;
  const worded = code ? t(key, { defaultValue: '' }) : '';
  if (code && worded) {
    const quota = data.quota || {};
    const spend = data.spend || {};
    return t(key, {
      used: formatCount(Number(quota.used ?? 0)),
      limit: formatCount(Number(data.limit ?? quota.limit ?? 0)),
      total: formatCount(Number(data.total ?? 0)),
      budget: formatUsd(Number(spend.budget_usd ?? 0)),
    });
  }
  const status = response?.status;
  if (!response) return t('insights.tpl.errors.network');
  if (status === 403) return t('insights.tpl.errors.forbidden');
  if (status === 404) return t('insights.tpl.errors.notFound');
  if (status === 429) return t('insights.tpl.errors.rateLimited');
  return t('insights.tpl.errors.generic');
}

/** The Insights filter row as query parameters (an agent option holds the
 *  ids of every same-named agent, comma-joined). */
export function filterParams(filters: InsightFilters): ScopeParams {
  return {
    date_from: filters.dateFrom || undefined,
    date_to: filters.dateTo || undefined,
    tenant_ids: filters.tenantIds.length ? filters.tenantIds : undefined,
    chatbot_ids: filters.chatbotIds.length ? filters.chatbotIds.flatMap((id) => id.split(',')) : undefined,
  };
}

/** Save a template's answers as CSV (the server names the file). Returns the
 *  refusal in the person's language, or null when the file was saved - an
 *  error body arrives as a Blob here, so it is read back first. */
export async function saveAnswersCsv(t: T, templateId: string, params: ScopeParams & { value?: string })
  : Promise<string | null> {
  try {
    const { blob, fileName } = await insightsService.downloadTemplateAnswers(templateId, params, 'answers.csv');
    triggerBlobDownload(blob, fileName);
    return null;
  } catch (err) {
    return errorMessage(t, err, (await blobErrorBody(err)) ?? undefined);
  }
}
